import { parseISO } from "date-fns";
import { AfrrBidDirectionEnum, AfrrBidStatusEnum } from "entities/affrEngagementService/types";

const UNKNOWN_OPERATOR = "UNKNOWN";

const DEFAULT_OPERATOR_COLOR = [11, 57, 134];

const OPERATOR_COLORS: Record<string, number[]> = {
  NWRE: DEFAULT_OPERATOR_COLOR,
  ELMY: [20, 140, 90],
};

export const prepareData = (points: any, dateRange: { startDate: Date; endDate: Date }) => {
  // Function to get the y value based on the point status and direction
  const getYValue = (point: any) => {
    // if the point is accepted or partially accepted, use the accepted quantity
    // else use the offered quantity
    const quantity =
      point.status === AfrrBidStatusEnum.ACCEPTED || point.status === AfrrBidStatusEnum.PARTIALLY_ACCEPTED
        ? point.accepted_quantity
        : point.offered_quantity;
    const yValue = +quantity;
    return point.direction === AfrrBidDirectionEnum.DOWNWARD ? -yValue : yValue;
  };

  const pointsByStatus = points.reduce((acc: any, point: any) => {
    // Parse the point dates and convert quantity and price fields to numbers
    const parsedPoint = {
      ...point,
      start_date: parseISO(point.start_date),
      end_date: parseISO(point.end_date),
      offered_quantity: +point.offered_quantity,
      offered_price: +point.offered_price,
      accepted_quantity: +point.accepted_quantity,
      accepted_price: +point.accepted_price,
      x: parseISO(point.start_date),
      y: getYValue(point),
    };

    if (Object.values(AfrrBidStatusEnum).includes(point.status)) {
      const key = `${point.status}_${point.direction}_${point.operator ?? UNKNOWN_OPERATOR}`;
      acc[key] = [...(acc[key] ?? []), parsedPoint];
    }

    return acc;
  }, {});

  // Function to combine points
  const combinePoints = (points: any[]) => {
    const combinedPointsMap = new Map<string, any>();

    points.forEach((point) => {
      const key = `${point.start_date.toISOString()}_${point.end_date.toISOString()}_${point.direction}`;

      if (combinedPointsMap.has(key)) {
        const existingPoint = combinedPointsMap.get(key);
        existingPoint.offered_quantity += point.offered_quantity;
        existingPoint.offered_price += point.offered_price;
        existingPoint.accepted_quantity += point.accepted_quantity;
        existingPoint.accepted_price += point.accepted_price;
        existingPoint.y += point.y;
      } else {
        combinedPointsMap.set(key, { ...point });
      }
    });

    return Array.from(combinedPointsMap.values());
  };

  // Combine points in each status
  Object.keys(pointsByStatus).forEach((status) => {
    pointsByStatus[status] = combinePoints(pointsByStatus[status]);
  });

  // Function to get the color for each dataset
  const getDatasetColor = (status: AfrrBidStatusEnum, operator: string) => {
    const [r, g, b] = OPERATOR_COLORS[operator.toUpperCase()] ?? DEFAULT_OPERATOR_COLOR;
    switch (status) {
      case AfrrBidStatusEnum.ACCEPTED:
        return `rgba(${r}, ${g}, ${b})`;
      case AfrrBidStatusEnum.PARTIALLY_ACCEPTED:
        return `rgba(${r}, ${g}, ${b}, 0.5)`;
      case AfrrBidStatusEnum.REJECTED:
        // Rejected bids: dark red color
        return "rgba(244, 138, 171)";
      case AfrrBidStatusEnum.NOT_SENT:
        // Not sent bids: light orange color
        return "rgba(230, 138, 46)";
      case AfrrBidStatusEnum.PENDING:
        // Pending bids: light blue color
        return "rgba(11, 99, 134, 0.7)";
      case AfrrBidStatusEnum.REFUSED:
        // Refused bids: red color
        return "rgba(182, 23, 40)";
      default:
        // Default color: black
        return "black";
    }
  };

  const statusOrder = [
    AfrrBidStatusEnum.ACCEPTED,
    AfrrBidStatusEnum.PARTIALLY_ACCEPTED,
    AfrrBidStatusEnum.NOT_SENT,
    AfrrBidStatusEnum.REFUSED,
    AfrrBidStatusEnum.REJECTED,
    AfrrBidStatusEnum.PENDING,
  ];
  const sortedKeys = Object.keys(pointsByStatus).sort(
    (a, b) =>
      statusOrder.indexOf(pointsByStatus[a][0].status) - statusOrder.indexOf(pointsByStatus[b][0].status) ||
      Number(a.endsWith("_ELMY")) - Number(b.endsWith("_ELMY")) ||
      a.localeCompare(b),
  );

  const datasets = sortedKeys.map((status) => ({
    label: status,
    data: pointsByStatus[status],
    backgroundColor: getDatasetColor(
      pointsByStatus[status][0].status,
      pointsByStatus[status][0].operator ?? UNKNOWN_OPERATOR,
    ),
    barPercentage: 1,
    categoryPercentage: 0.9,
  }));

  // create a function returning an array of all hours between dateRange.start and dateRange.end
  const getHoursBetweenDates = (dateRangeObj: { startDate: Date; endDate: Date }) => {
    const hours = [];
    for (let i = dateRangeObj.startDate.getTime(); i < dateRangeObj.endDate.getTime(); i += 60 * 60 * 1000) {
      hours.push(new Date(i));
    }
    return hours;
  };

  // Get all hours between the start and end dates
  const labels = getHoursBetweenDates(dateRange);

  return { labels, datasets }; // Return the prepared labels and datasets
};
